// Хранилища платформы «десктоп»: секреты — в хранилище ключей системы, база — нативный SQLite с
// шифрованием страниц (SQLCipher). Интерфейсы те же, что у браузерных реализаций, поэтому ядро о
// разнице не знает и не должно знать.

import type { Database, Row, SecretStore, SqlValue } from "@yofis/core";

import type { DesktopShell } from "./shell.ts";

/** Секреты через хранилище системы; если шифрования нет, оболочка откажет — и скажет, почему. */
export class ShellSecretStore implements SecretStore {
  readonly #shell: DesktopShell;

  constructor(shell: DesktopShell) {
    this.#shell = shell;
  }

  get(key: string): Promise<string | undefined> {
    return this.#shell.secrets.get(key);
  }

  set(key: string, value: string): Promise<void> {
    return this.#shell.secrets.set(key, value);
  }

  delete(key: string): Promise<void> {
    return this.#shell.secrets.delete(key);
  }
}

/**
 * Локальная база десктопа: файл SQLite с шифрованием страниц, живущий в главном процессе.
 *
 * Вызовы синхронные намеренно. Интерфейс `Database` в ядре синхронный, потому что в вебе база лежит
 * прямо во вкладке; делать его асинхронным ради особенности оболочки значило бы переписать слой
 * хранения ядра под Electron — то есть ровно то, чего «тонкая оболочка» не должна требовать.
 */
export class ShellDatabase implements Database {
  readonly #shell: DesktopShell;

  private constructor(shell: DesktopShell) {
    this.#shell = shell;
  }

  /** Открывает файл базы; если в системе нет хранилища ключей, оболочка откажет с объяснением. */
  static async open(shell: DesktopShell, name = "yofis"): Promise<ShellDatabase> {
    await shell.database.open(name);
    return new ShellDatabase(shell);
  }

  run(sql: string, params: readonly SqlValue[] = []): void {
    const res = this.#shell.database.run(sql, [...params]);
    if (!res.ok) {
      throw new Error(res.error ?? "запрос к локальной базе не выполнен");
    }
  }

  all(sql: string, params: readonly SqlValue[] = []): Row[] {
    const res = this.#shell.database.all(sql, [...params]);
    if (!res.ok) {
      throw new Error(res.error ?? "запрос к локальной базе не выполнен");
    }
    return (res.rows ?? []) as Row[];
  }

  get(sql: string, params: readonly SqlValue[] = []): Row | undefined {
    return this.all(sql, params)[0];
  }

  /** Вложенные вызовы разделяют внешнюю транзакцию — как и в реализации на WebAssembly. */
  transaction<T>(body: () => T): T {
    if (this.#depth > 0) {
      this.#depth++;
      try {
        return body();
      } finally {
        this.#depth--;
      }
    }
    this.run("BEGIN");
    this.#depth = 1;
    try {
      const result = body();
      this.run("COMMIT");
      return result;
    } catch (err) {
      this.run("ROLLBACK");
      throw err;
    } finally {
      this.#depth = 0;
    }
  }

  #depth = 0;

  /** Выгрузка байтов нужна вебу (сохранить базу в OPFS); на десктопе база и так лежит файлом. */
  export(): Uint8Array {
    return new Uint8Array();
  }

  close(): void {
    // Файл закрывает оболочка при выходе: рендерер может быть перезагружен, а база должна пережить это.
  }
}
